import React, { useState, useEffect } from "react"
import { Routes, Route } from "react-router-dom"
import { ConfigProvider, Typography, App as AntApp } from "antd"

import setUser from "@helpers/setUser"
import parseJwt from "@helpers/parseJwt"
import useMessage from "@components/useMessage"
import ScrollToTop from "@components/ScrollToTop"
import NotFoundPage from "@components/NotFoundPage"
import ThankYouPage from "@components/ThankYouPage"
import IndexRedirect from "@components/IndexRedirect"
import getThemeToken from "@helpers/getThemeToken"
import { AppContext } from "@components/AppContext"
import LogoutRedirect from "@components/LogoutRedirect"
import TermsOfService from "@components/TermsOfService/TermsOfService"
import useIdentityStore from "@stores/useIdentityStore"
import AuthorizationError from "@components/AuthorizationError"
import setApplicationTitle from "@helpers/setApplicationTitle"
import useRequest, { useAuthenticatedRequest } from "@api/useRequest"
import authorize, { authorizeUser, authorizeInvestor } from "@api/authorize"
import authenticate, { ERROR_UNAUTHORIZED, setIsPendingAuthentication } from "@api/authenticate"

import BackstageLayout from "@layouts/backstage/BackstageLayout"
import BackstageProjectLayout from "@layouts/backstage/BackstageProjectLayout"
import BackstageSettingsLayout from "@layouts/backstage/BackstageSettingsLayout"

import InvestorLayout from "@layouts/investor/InvestorLayout"
import InvestorProjectLayout from "@layouts/investor/InvestorProjectLayout"
import InvestorInvestmentLayout from "@layouts/investor/InvestorInvestmentLayout"

import CreateCommitmentPage from "@modules/investor/commitment/CreateCommitmentPage"

import InvestmentAccountPage from "@modules/investor/investment/InvestmentAccountPage"
import InvestmentProfilesPage from "@modules/investor/investment/InvestmentProfilesPage"
import InvestmentFinalizePage from "@modules/investor/investment/InvestmentFinalizePage"

import InvestorAccountPage from "@modules/investor/InvestorAccountPage"
import InvestorDashboardPage from "@modules/investor/InvestorDashboardPage"
import InvestorDocumentsPage from "@modules/investor/InvestorDocumentsPage"
import InvestorTransactionsPage from "@modules/investor/InvestorTransactionsPage"
import InvestorProjectDetailsPage from "@modules/investor/InvestorProjectDetailsPage"
import InvestorProjectInvestmentsPage from "@modules/investor/InvestorProjectInvestmentsPage"

import AccountsPage from "@modules/backstage/investors/AccountsPage"
import ActivitiesPage from "@modules/backstage/activities/ActivitiesPage"
import PropertiesPage from "@modules/backstage/properties/PropertiesPage"
import TransactionsPage from "@modules/backstage/transactions/TransactionsPage"
import ArchivedProjectsPage from "@modules/backstage/projects/ArchivedProjectsPage"
import { CampaignsPage, CampaignUpdatePage } from "@modules/backstage/campaigns"

import ProjectDetailsPage from "@modules/backstage/projects/ProjectDetailsPage"
import ProjectDocumentsPage from "@modules/backstage/projects/ProjectDocumentsPage"
import ProjectInvestmentsPage from "@modules/backstage/projects/ProjectInvestmentsPage"
import ProjectDistributionsPage from "@modules/backstage/projects/ProjectDistributionsPage"

import SettingsMembersPage from "@modules/backstage/settings/SettingsMembersPage"
import SettingsBrokersPage from "@modules/backstage/settings/SettingsBrokersPage"
import SettingsSourcesPage from "@modules/backstage/settings/SettingsSourcesPage"
import SettingsOrganizationPage from "@modules/backstage/settings/SettingsOrganizationPage"
import SettingsEmailIdentityPage from "@modules/backstage/settings/SettingsEmailIdentityPage"

import ConsoleLayout from "@layouts/console/ConsoleLayout"

import UsersPage from "@modules/console/users/UsersPage"
import CustomersPage from "@modules/console/customers/CustomersPage"
import OrganizationsPage from "@modules/console/organizations/OrganizationsPage"

import { W0, isBackstage } from "@components/constants"

import "./App.css"

const { Paragraph } = Typography

const ERROR_USER_NOT_FOUND = "DocumentNotFoundError"

const LABEL_REFRESH = "Refresh authorization..."
const LABEL_AUTHORIZING = "Authorizing..."
const LABEL_ACCESS_DENIED = "Access denied"


const App = () => {
  const [ isRoot, setIsRoot ] = useState()
  const [ authentication, setAuthentication ] = useState()
  const [ authorizationError, setAuthorizationError ] = useState()
  const [ authenticationError, setAuthenticationError ] = useState()

  const [
    messageContextHolder,
    showErrorMessage,
    showSuccessMessage,
    showLoadingMessage
  ] = useMessage()

  useEffect(() => {
    if (!authentication) {
      return
    }

    const payload = parseJwt(authentication)
    setIsRoot(payload.isRoot)
  }, [ authentication ])

  const isGuest = window.location.pathname.startsWith("/scf")

  const authorizeOrganizationIdentity = (organizationId, authenticationJwt = authentication) =>
    authorize(setIdentity, onAuthorizationError, authorizationMethod, authenticationJwt, organizationId)

  const { authorizeIdentity, identity, setIdentity, ...restIdentityStore } = useIdentityStore(authorizeOrganizationIdentity)

  const refreshAuthentication = () => {
    const [ messageHolder ] = messageContextHolder
    const shouldRenderLoading = !!messageHolder.ref.current

    const refresh = () =>
      authenticate(setAuthenticationError, isBackstage, true)
        .then(authenticationJwt => setAuthentication(authenticationJwt))

    setIsPendingAuthentication()

    return shouldRenderLoading
      ? showLoadingMessage(LABEL_REFRESH, refresh)
      : refresh()
  }

  window.refreshAuthentication = refreshAuthentication

  const onAuthorizationError = error => {
    const isAccessDenied = error.code === ERROR_USER_NOT_FOUND
    const isAuthenticationExpired = error.code === ERROR_UNAUTHORIZED

    if (isAccessDenied) {
      return setAuthorizationError(LABEL_ACCESS_DENIED)
    }

    if (isAuthenticationExpired) {
      // NOTE: signOut if impersonated session:
      return refreshAuthentication()
    }

    setAuthorizationError(error.originalError?.message || error.message)
  }

  const onUnexpectedError = error =>
    showErrorMessage(error.message, error.originalError)

  const authorizationMethod = isBackstage
    ? authorizeUser
    : authorizeInvestor

  const request = useRequest(setIdentity, onUnexpectedError, onAuthorizationError, authorizationMethod, authentication)
  const authenticatedRequest = useAuthenticatedRequest(isBackstage, onUnexpectedError)

  useEffect(() => {
    if (isGuest) {
      return setAuthentication(null)
    }

    authenticate(setAuthenticationError, isBackstage)
      .then(authenticationJwt => setAuthentication(authenticationJwt))
  }, [ isBackstage ])

  useEffect(() => {
    if (!authentication) { return }

    if (identity) {
      setUser(identity)
      return
    }

    authorizeIdentity()
  }, [ authentication, isBackstage, identity ])

  const isLoading = authentication === undefined

  const isAuthenticated = !!authentication

  const labelStyle = { paddingTop: W0 / 2, paddingLeft: W0 }

  if (isLoading) {
    return <Paragraph style={labelStyle}>{LABEL_AUTHORIZING}</Paragraph>
  }

  if (!isAuthenticated && !isGuest) {
    return (
      <Paragraph>
        {
          authenticationError && (
            <AuthorizationError
              message={authenticationError}
              isBackstage={isBackstage}
            />
          )
        }

        {
          !authenticationError && (
            <LogoutRedirect isBackstage={isBackstage} />
          )
        }
      </Paragraph>
    )
  }

  if (!identity && !isGuest) {
    if (authorizationError) {
      return (
        <AuthorizationError
          message={authorizationError}
          isBackstage={isBackstage}
        />
      )
    }

    return <Paragraph style={labelStyle}>{LABEL_AUTHORIZING}</Paragraph>
  }

  const shouldShowTermsOfService =
    false &&
    (!isGuest &&
    !!identity &&
    !identity.isLatestTermsAccepted)

  if (shouldShowTermsOfService) {
    return (
      <TermsOfService
        request={request}
        identityId={identity.id}
        isBackstage={isBackstage}
        afterAccepted={authorizeIdentity}
      />
    )
  }

  setApplicationTitle(identity)
  const token = getThemeToken(isBackstage)

  const context = {
    isRoot,
    request,
    identity,
    setIdentity,
    showErrorMessage,
    showLoadingMessage,
    setAuthentication,
    showSuccessMessage,
    authenticatedRequest,
    authorizeIdentity,
    ...restIdentityStore
  }

  const renderOrganizationBackstage = () =>
    <Route path="/backstage" element={<BackstageLayout {...context} />}>
      <Route index element={<IndexRedirect to="investors" />} />

      <Route path="investors" element={<AccountsPage />} />
      <Route path="investors/create" element={<AccountsPage />} />
      <Route path="investors/:investorId" element={<AccountsPage />} />

      <Route path="transactions" element={<TransactionsPage />} />

      <Route path="activities" element={<ActivitiesPage />} />

      <Route path="properties" element={<PropertiesPage />} />

      <Route path="projects/archived" element={<ArchivedProjectsPage />} />

      <Route path="settings" element={<BackstageSettingsLayout />}>
        <Route path="sources" element={<SettingsSourcesPage /> } />
        <Route path="members" element={<SettingsMembersPage /> } />
        <Route path="brokers" element={<SettingsBrokersPage /> } />
        <Route path="organization" element={<SettingsOrganizationPage /> } />
        <Route path="email-identity" element={<SettingsEmailIdentityPage /> } />
      </Route>

      <Route path="project/:id" element={<BackstageProjectLayout />}>
        <Route path="details" element={<ProjectDetailsPage /> } />
        <Route path="documents" element={<ProjectDocumentsPage /> } />
        <Route path="investments" element={<ProjectInvestmentsPage /> } />
        <Route path="investments/:investmentsId" element={<ProjectInvestmentsPage /> } />
        <Route path="distributions" element={<ProjectDistributionsPage /> } />
      </Route>

      <Route path="campaigns" element={<CampaignsPage />} />
      <Route path="campaigns/:campaignId" element={<CampaignUpdatePage />} />

      <Route path="404" element={<NotFoundPage />} />
    </Route>

  const renderPlatformBackstage = () =>
    <Route path="/backstage" element={<ConsoleLayout {...context} />}>
      <Route index element={<IndexRedirect to="organizations" />} />

      <Route path="organizations" element={<OrganizationsPage />} />
      <Route path="customers" element={<CustomersPage />} />
      <Route path="users" element={<UsersPage />} />
      <Route path="activities" element={<ActivitiesPage />} />
    </Route>

  const renderBackstage = () =>
    restIdentityStore.isConsoleOrganization
      ? renderPlatformBackstage()
      : renderOrganizationBackstage()

  return (
    <React.StrictMode>
      <AntApp>
        <ScrollToTop />

        <ConfigProvider theme={{ token }}>
          {messageContextHolder}

          <AppContext.Provider value={context}>
            <Routes>
              <Route path="/logout/*" element={<LogoutRedirect isBackstage={isBackstage} />} />

              <Route path="/scf/thank-you" element={<ThankYouPage />} />

              <Route path="/scf/create/:projectId/:sponsorId" element={<CreateCommitmentPage />} />

              <Route path="/investments/:projectId" element={<InvestorInvestmentLayout />}>
                <Route index element={<InvestmentAccountPage />} />
                <Route path=":action/:investmentId?" element={<InvestmentProfilesPage />} />
                <Route path="sign" element={<InvestmentFinalizePage />} />
                <Route path="sign/:investmentId" element={<InvestmentFinalizePage />} />
              </Route>

              <Route path="/" element={<InvestorLayout />}>
                <Route index element={<IndexRedirect to="dashboard" />} />

                <Route path="dashboard" element={<InvestorDashboardPage />} />

                <Route path="projects/:projectId" element={<InvestorProjectLayout />}>
                  <Route path="about" element={<InvestorProjectDetailsPage />} />
                  <Route path="documents" element={<InvestorDocumentsPage />} />
                  <Route path="investments" element={<InvestorProjectInvestmentsPage />} />
                  <Route path="distributions" element={<InvestorTransactionsPage />} />
                </Route>

                <Route path="account" element={<InvestorAccountPage />} />
                <Route path="documents" element={<InvestorDocumentsPage />} />
                <Route path="transactions" element={<InvestorTransactionsPage />} />

                <Route path="404" element={<NotFoundPage />} />
              </Route>

              {renderBackstage()}
            </Routes>
          </AppContext.Provider>
        </ConfigProvider>
      </AntApp>
    </React.StrictMode>
  )
}

export default App
